import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import debounce from "lodash.debounce";
import { ProductContext } from "../../../store";
import DetailSkeleton from "../../../components/Skeleton/DetailSkeleton";
import RecommendationsGrid from "../../../components/AiVisibility/WritingAssistance/RecommendationsGrid/RecommendationsGrid";
import {
	EMPTY_RECOMMENDATION_FILTERS,
	recommendationFiltersToApiParams,
} from "../../../components/AiVisibility/WritingAssistance/RecommendationsGrid/recommendationGridUtils";
import {
	listWritingAssistantRecommendationsList,
	submitWritingAssistantJob,
} from "../../../services/writingAssistantApi";
import { listAiVisibilityAudits } from "../../../services/aiVisibilityApi";

function writingPreviewPath(jobId) {
	return `/aivisibility/writing-assistance/view?jobId=${encodeURIComponent(jobId)}`;
}

const AiVisibilityWritingRecommendations = () => {
	const navigate = useNavigate();
	const { currentWebsiteId, userLoader, websiteLoader } = useContext(ProductContext);

	const [items, setItems] = useState([]);
	const [total, setTotal] = useState(0);
	const [skip, setSkip] = useState(0);
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(20);
	const [loading, setLoading] = useState(true);
	const [actionLoading, setActionLoading] = useState(null);
	const [filters, setFilters] = useState(EMPTY_RECOMMENDATION_FILTERS);
	const [debouncedTitle, setDebouncedTitle] = useState("");
	const [debouncedUrl, setDebouncedUrl] = useState("");
	const [auditOptions, setAuditOptions] = useState([]);

	const applyDebouncedTitle = useMemo(
		() =>
			debounce((value) => {
				setDebouncedTitle(String(value || "").trim());
			}, 300),
		[]
	);

	const applyDebouncedUrl = useMemo(
		() =>
			debounce((value) => {
				setDebouncedUrl(String(value || "").trim());
			}, 300),
		[]
	);

	useEffect(() => {
		return () => {
			applyDebouncedTitle.cancel();
			applyDebouncedUrl.cancel();
		};
	}, [applyDebouncedTitle, applyDebouncedUrl]);

	useEffect(() => {
		let cancelled = false;
		async function loadAudits() {
			if (!currentWebsiteId) {
				setAuditOptions([]);
				return;
			}
			const result = await listAiVisibilityAudits(currentWebsiteId, 0, 100);
			if (cancelled) return;
			if (result.ok) {
				setAuditOptions(result.audits || []);
			} else {
				setAuditOptions([]);
			}
		}
		loadAudits();
		return () => {
			cancelled = true;
		};
	}, [currentWebsiteId]);

	const apiFilters = useMemo(
		() =>
		  recommendationFiltersToApiParams({
			category: filters.category,
			severity: filters.severity,
			impact: filters.impact,
			jobstatus: filters.jobstatus,
			auditId: filters.auditId,
			title: debouncedTitle,
			url: debouncedUrl,
		  }),
		[
		  filters.category,
		  filters.severity,
		  filters.impact,
		  filters.jobstatus,
		  filters.auditId,
		  debouncedTitle,
		  debouncedUrl,
		]
	  );

	const fetchList = useCallback(async () => {
		if (!currentWebsiteId) {
			setItems([]);
			setTotal(0);
			setSkip(0);
			setLoading(false);
			return;
		}
		setLoading(true);
		const nextSkip = (page - 1) * pageSize;
		const result = await listWritingAssistantRecommendationsList(currentWebsiteId, {
			skip: nextSkip,
			limit: pageSize,
			sortDir: "desc",
			...apiFilters,
		});
		if (!result.ok) {
			toast.error(result.message || "Failed to load recommendations");
			setItems([]);
			setTotal(0);
			setSkip(nextSkip);
		} else {
			setItems(result.items || []);
			setTotal(result.total || 0);
			setSkip(result.skip ?? nextSkip);
		}
		setLoading(false);
	}, [currentWebsiteId, page, pageSize, apiFilters]);

	useEffect(() => {
		setPage(1);
	}, [currentWebsiteId]);

	useEffect(() => {
		fetchList();
	}, [fetchList]);

	const handleFiltersChange = (next) => {
		const selectChanged =
		  next.category !== filters.category ||
		  next.severity !== filters.severity ||
		  next.impact !== filters.impact ||
		  next.jobstatus !== filters.jobstatus ||
		  next.auditId !== filters.auditId;
		setFilters(next);
		applyDebouncedTitle(next.title);
		applyDebouncedUrl(next.url);
		if (selectChanged) {
		  applyDebouncedTitle.flush();
		  applyDebouncedUrl.flush();
		  setPage(1); // only for dropdowns; text waits for debounce
		}
	  };

	const handleClearFilters = () => {
		applyDebouncedTitle.cancel();
		applyDebouncedUrl.cancel();
		setFilters(EMPTY_RECOMMENDATION_FILTERS);
		setDebouncedTitle("");
		setDebouncedUrl("");
		setPage(1);
	};
	useEffect(() => {
		setPage(1);
	  }, [debouncedTitle, debouncedUrl]);
	const handlePageChange = (nextPage) => {
		setPage(Math.max(1, nextPage));
	};

	const handlePageSizeChange = (nextSize) => {
		setPageSize(nextSize);
		setPage(1);
	};

	const handleView = (item) => {
		if (item?.writing_job_id == null) return;
		navigate(writingPreviewPath(item.writing_job_id));
	};

	const handleGenerate = async (item, rowId) => {
		if (!currentWebsiteId || !item?.url) return;
		setActionLoading(`generate-${rowId}`);
		try {
			const result = await submitWritingAssistantJob(currentWebsiteId, {
				auditId: item.audit_id,
				recommendationId: item.recommendation_id,
				url: item.url,
			});
			if (!result.ok || !result.job?.id) {
				toast.error(result.message || "Failed to submit job");
				return;
			}
			navigate(writingPreviewPath(result.job.id));
		} catch {
			toast.error("Failed to generate fix");
		} finally {
			setActionLoading(null);
		}
	};

	if (userLoader || websiteLoader) {
		return <DetailSkeleton />;
	}

	return (
		<div className="aivisibility-page writing-recommendations-page">
			<RecommendationsGrid
				items={items}
				total={total}
				skip={skip}
				page={page}
				pageSize={pageSize}
				loading={loading}
				actionLoading={actionLoading}
				filters={filters}
				auditOptions={auditOptions}
				onFiltersChange={handleFiltersChange}
				onClearFilters={handleClearFilters}
				onPageChange={handlePageChange}
				onPageSizeChange={handlePageSizeChange}
				onView={handleView}
				onGenerate={handleGenerate}
			/>
		</div>
	);
};

export default AiVisibilityWritingRecommendations;
